import { useQueryClient } from "@tanstack/react-query";
import {
  CreateBase,
  Form,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useRedirect,
  useTranslate,
  type GetListResult,
} from "ra-core";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { FormToolbar } from "../layout/FormToolbar";
import type { Deal } from "../types";
import { DealInputs } from "./DealInputs";
import { firstOpenStage } from "./stages";
import {
  ALL_PROJECTS,
  useCurrentDealPipeline,
  useCurrentProject,
} from "../projects/useCurrentProject";

export const DealCreate = ({ open }: { open: boolean }) => {
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const { data: allDeals } = useListContext<Deal>();

  const handleClose = () => {
    redirect("/deals");
  };

  const queryClient = useQueryClient();

  const onSuccess = async (deal: Deal) => {
    if (!allDeals) {
      redirect("/deals");
      return;
    }
    // increase the index of all deals in the same stage as the new deal
    // first, get the list of deals in the same stage
    const deals = allDeals.filter(
      (d: Deal) => d.stage_id === deal.stage_id && d.id !== deal.id,
    );
    // update the actual deals in the database
    await Promise.all(
      deals.map(async (oldDeal) =>
        dataProvider.update("deals", {
          id: oldDeal.id,
          data: { index: oldDeal.index + 1 },
          previousData: oldDeal,
        }),
      ),
    );
    // refresh the list of deals in the cache as we used dataProvider.update(),
    // which does not update the cache
    const dealsById = deals.reduce(
      (acc, d) => ({
        ...acc,
        [d.id]: { ...d, index: d.index + 1 },
      }),
      {} as { [key: string]: Deal },
    );
    const now = Date.now();
    queryClient.setQueriesData<GetListResult | undefined>(
      { queryKey: ["deals", "getList"] },
      (res) => {
        if (!res) return res;
        return {
          ...res,
          data: res.data.map((d: Deal) => dealsById[d.id] || d),
        };
      },
      { updatedAt: now },
    );
    redirect("/deals");
  };

  const { identity } = useGetIdentity();
  // project and pipeline from the header switcher and the board tabs; with
  // «Все проекты» the form asks for the project
  const { currentId } = useCurrentProject();
  const projectId = currentId === ALL_PROJECTS ? null : currentId;
  const { pipeline, stages } = useCurrentDealPipeline(projectId);

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent className="lg:max-w-4xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
        <CreateBase resource="deals" mutationOptions={{ onSuccess }}>
          <CreateHeader />
          <Form
            defaultValues={{
              sales_id: identity?.id,
              contact_ids: [],
              index: 0,
              project_id: projectId ?? undefined,
              pipeline_id: pipeline?.id,
              stage_id: stages.length ? firstOpenStage(stages).id : undefined,
            }}
          >
            <DealInputs chooseProject={projectId == null} />
            <FormToolbar />
          </Form>
        </CreateBase>
      </DialogContent>
    </Dialog>
  );
};

const CreateHeader = () => {
  const translate = useTranslate();
  return (
    <DialogTitle className="text-xl font-semibold">
      {translate("resources.deals.action.create")}
    </DialogTitle>
  );
};
